"use client";

import { AnimatedElement } from "@/components/marketing/AnimatedSection";
import { Check, X } from "lucide-react";
import { useLang } from "@/contexts/language-context";

/**
 * "Qué es y qué no es Redy Capital."
 *
 * Esta sección existe por obligación legal —hay que dejar claro que Redy no
 * presta— pero funciona además como el mejor argumento de la página. En una
 * categoría donde todo el mundo promete dinero fácil, decir en grande lo que
 * NO haces es lo que hace creíble lo que sí. No suavizar sin abogado.
 */
export default function CapitalHonesty() {
  const { t } = useLang();

  const is = [
    t("A read of your real sales, measured order by order.", "Una lectura de tus ventas reales, medidas orden por orden."),
    t("A filter that tells you which options your numbers reach before you apply.", "Un filtro que te dice qué opciones alcanzan tus números antes de solicitar."),
    t("An introduction to SmallBusinessLoans, North's financing partner.", "Una presentación con SmallBusinessLoans, socio de financiamiento de North."),
    t("Free for every Redy merchant, with no effect on your plan or your rates.", "Gratis para todo comercio de Redy, sin efecto en tu plan ni en tus tarifas."),
  ];

  const isNot = [
    t("A loan from Redy. We do not lend money and never touch the funds.", "Un préstamo de Redy. No prestamos dinero ni tocamos los fondos."),
    t("An approval. Meeting the sales minimum is not a credit decision.", "Una aprobación. Alcanzar el mínimo de ventas no es una decisión de crédito."),
    t("A quote. We do not set rates, terms or amounts, and we do not negotiate them.", "Una cotización. No fijamos tasas, plazos ni montos, y no los negociamos."),
    t("Advice. We will not tell you which product to take — that is between you and the lender.", "Un consejo. No te decimos cuál producto tomar — eso es entre tú y el prestamista."),
  ];

  return (
    <section className="relative py-24 px-6 bg-white">
      <div className="max-w-[1200px] mx-auto">
        <div className="max-w-[660px] mb-14">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">
            {t("Straight talk", "Sin letra chica")}
          </div>
          <h2 className="text-4xl md:text-[52px] font-black tracking-tight leading-[1.05] text-redy-charcoal">
            {t("What this is. And what it isn't.", "Qué es esto. Y qué no es.")}
          </h2>
          <p className="text-lg text-redy-secondary mt-5 leading-relaxed font-medium">
            {t(
              "Everyone in this category promises easy money. We would rather tell you exactly where our part ends.",
              "Todo el mundo en esta categoría promete dinero fácil. Preferimos decirte exactamente dónde termina nuestra parte."
            )}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <AnimatedElement
            delay={0}
            className="rounded-2xl border border-black/[0.07] p-7 md:p-8"
            style={{ background: "#F7F5F1" }}
          >
            <h3 className="text-[13px] font-black tracking-[0.14em] uppercase text-redy-charcoal mb-6">
              {t("What Redy Capital is", "Qué es Redy Capital")}
            </h3>
            <ul className="space-y-4">
              {is.map((line) => (
                <li key={line} className="flex gap-3">
                  <span
                    className="shrink-0 w-5 h-5 rounded-full grid place-items-center mt-0.5"
                    style={{ background: "rgba(46,204,113,0.14)" }}
                    aria-hidden
                  >
                    <Check size={12} strokeWidth={3.5} color="#2D7A3A" />
                  </span>
                  <span className="text-[15px] text-redy-charcoal leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          </AnimatedElement>

          <AnimatedElement
            delay={0.1}
            className="rounded-2xl border border-black/[0.07] p-7 md:p-8 bg-white"
          >
            <h3 className="text-[13px] font-black tracking-[0.14em] uppercase text-redy-charcoal mb-6">
              {t("What it is not", "Qué no es")}
            </h3>
            <ul className="space-y-4">
              {isNot.map((line) => (
                <li key={line} className="flex gap-3">
                  <span
                    className="shrink-0 w-5 h-5 rounded-full grid place-items-center mt-0.5"
                    style={{ background: "rgba(0,0,0,0.05)" }}
                    aria-hidden
                  >
                    <X size={12} strokeWidth={3.5} color="#9B9690" />
                  </span>
                  <span className="text-[15px] text-redy-secondary leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          </AnimatedElement>
        </div>

        {/* Divulgación de la relación económica. Va visible, no escondida. */}
        <div className="mt-10 rounded-2xl border border-black/[0.07] p-6 md:p-7" style={{ background: "#FCFAF6" }}>
          <p className="text-[13.5px] text-redy-secondary leading-relaxed max-w-[860px]">
            <span className="font-bold text-redy-charcoal">
              {t("How Redy gets paid:", "Cómo cobra Redy:")}
            </span>{" "}
            {t(
              "Redy receives a commission from SmallBusinessLoans when a referred merchant obtains financing. That does not change your terms, and it is why we will not recommend one product over another. The lender evaluates your personal credit history and your time in business, requests three months of bank statements and a signed application, and decides on its own.",
              "Redy recibe una comisión de SmallBusinessLoans cuando un comercio referido obtiene financiamiento. Eso no cambia tus condiciones, y es la razón por la que no recomendamos un producto sobre otro. El prestamista evalúa tu historial de crédito personal y la antigüedad de tu negocio, pide tres meses de estados de cuenta y una solicitud firmada, y decide por su cuenta."
            )}
          </p>
        </div>
      </div>
    </section>
  );
}
